// @flow

import clsx from 'clsx';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'src/hooks/useAppSelector';
import type { FormProps } from 'redux-form';

import { Intro } from 'gandi.react-public-layout';

import { withPageMeta } from '@gandi/app-runner/dist/utils/withPageMeta';
import { mapTags, useTranslator } from '@gandi/translate';
import { Alert, Base, Spinner } from '@gandi/ui';

import { compose } from 'redux';
import * as url from 'url';
import withForm from 'gandi.react-form/dist/components/wrappers/withForm';

import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';

import Layout from '../containers/Layout';
import UnsubscriptionForm from './UnsubscriptionForm';

import {
  fetchUnsubscriptionInfos,
  resource,
  selectInfosUnsubscription,
  submitUnsubscriptionForm,
} from './modules';
import styles from './Unsubscription.scss';
import AlertError from '../components/AlertError';
import type { HandleForm, PreFetch } from '../types';

type InjectedProps = {|
  ...WithRouterProps<{|
    token: string,
  |}>,
  reduxFormProps: FormProps,
|};
export type Props = {|
  ...InjectedProps,
|};

function Unsubscription({
  location,
  params,
  reduxFormProps,
  router,
}: Props): React.Element<typeof Layout> {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const infos = useAppSelector(selectInfosUnsubscription);

  const shouldFetch = useAppSelector(resource.selectShouldFetchOne);
  const isFailure = useAppSelector(resource.selectIsFailureOne);
  const isLoaded = useAppSelector(resource.selectIsLoadedOne);
  const isFetching = useAppSelector(resource.selectIsFetchingOne);

  React.useEffect(() => {
    if (shouldFetch) {
      dispatch(fetchUnsubscriptionInfos(params.token));
    }
  }, [dispatch, params.token, shouldFetch]);

  const missingInfos = !infos.display_name || !infos.subscription_key;

  return (
    <Layout location={location}>
      <Base className={clsx('wrapper-sm', styles.center)}>
        <Intro title={__('Update your notification preferences')} />
        {isFetching && !isLoaded && (
          <Base marginBottom="base">
            <Spinner centered />
          </Base>
        )}
        {isFailure && !isFetching && (
          <Alert
            message={__(
              'You may have already unsubscribed from this notification, or followed a bad link'
            )}
            intent="info"
          />
        )}
        {isLoaded && missingInfos && <AlertError />}
        {isLoaded && !missingInfos && (
          <>
            <Base tagName="p" marginBottom="base">
              {mapTags(
                __(
                  'You received an email from Gandi about a [[type:]] for [[email:]] related to [[orgaName:]]'
                ),
                () => ({
                  type: (
                    <strong>{infos.label || infos.subscription_key}</strong>
                  ),
                  email: <strong>{infos.address}</strong>,
                  orgaName: <strong>{infos.display_name}</strong>,
                })
              )}
            </Base>
            <UnsubscriptionForm
              reduxFormProps={reduxFormProps}
              params={params}
              router={router}
              infos={infos}
            />
            <Base tagName="p">
              {__(
                'You will not receive these email notifications anymore, unless someone with the necessary permissions adds you back to the list of recipients.'
              )}
            </Base>
          </>
        )}
      </Base>
    </Layout>
  );
}

Unsubscription.preFetch = function preFetch(args: PreFetch<Props>) {
  return args.dispatch(fetchUnsubscriptionInfos(args.params.token));
};

const handleForm = ({
  dispatch,
  req,
  res,
  params,
  translator: { __ },
}: HandleForm<Required<InjectedProps>>) => {
  // $FlowFixMe[incompatible-type] mixed body
  const data: {| unsubscription_choice?: string |} = req.body || {};

  const currentUrl = url.format({
    protocol: req.protocol,
    hostname: req.hostname,
    pathname: req.path,
    query: req.query,
  });

  const promises = [
    // $FlowFixMe[incompatible-call] unsubscription_choice can be missing
    dispatch(submitUnsubscriptionForm(data, params, __, req, res, currentUrl)), // send data to the API and redirect
  ];
  return Promise.all(promises);
};

export default (compose(
  // $FlowFixMe[underconstrained-implicit-instantiation]
  withRouter,
  withPageMeta(({ __ }) => ({
    title: __('Email notification subscription cancellation - Gandi.net'),
    robots: 'noindex',
  })),
  withForm({
    form: 'UnsubscriptionForm',
    propNamespace: 'reduxFormProps',
    serverHandler: handleForm,
  })
)(Unsubscription): React.ComponentType<{||}>);
